import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, HandCoins, Leaf } from "lucide-react";
import { useLang } from "../../i18n";
import { useContent } from "../../lib/useContent";
import { resolveMedia } from "../../lib/api";
import { MaskedLine } from "../Reveal";

export default function Hero() {
  const { t } = useLang();
  const c = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden bg-ngo-forest">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={resolveMedia(c.hero_image)}
          alt="Anansha Welfare Society की सामुदायिक गतिविधि — वाराणसी, उत्तर प्रदेश"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-ngo-forest/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-ngo-forest via-ngo-forest/30 to-transparent" />

      <Leaf className="absolute top-24 right-[12%] w-10 h-10 text-ngo-mint/20 leaf-float hidden md:block" aria-hidden="true" />
      <Leaf className="absolute bottom-40 left-[6%] w-7 h-7 text-ngo-gold/30 leaf-float hidden md:block" style={{ animationDelay: "2s" }} aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-6 py-28 w-full">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="text-ngo-gold text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-6"
        >
          {c.reg_no} • {t("top.est")} {c.established}
        </motion.p>

        <h1 data-testid="hero-title" className="font-heading font-black text-ngo-cream text-4xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.28] sm:leading-[1.22] tracking-tight max-w-5xl">
          <MaskedLine delay={0.3}>{t("hero.line1")}</MaskedLine>
          <MaskedLine delay={0.45} className="text-ngo-gold">{t("hero.line2")}</MaskedLine>
          <MaskedLine delay={0.6}>{t("hero.line3")}</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-ngo-mint/85"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/donate"
            data-testid="hero-donate-btn"
            className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-ngo-gold text-ngo-ink font-semibold hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(216,169,40,0.45)] transition-[transform,box-shadow] duration-300"
          >
            <HandCoins size={17} className="transition-transform duration-300 group-hover:-rotate-12 group-hover:scale-110" />
            {t("hero.cta2")}
          </Link>
          <Link
            to="/our-work"
            data-testid="hero-work-btn"
            className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-ngo-cream text-ngo-forest font-semibold hover:bg-white hover:-translate-y-1 transition-[background-color,transform] duration-300"
          >
            {t("hero.cta1")}
            <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>

        <motion.p
          data-testid="hero-trust-strip"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-10 text-xs sm:text-sm text-ngo-mint/60 tracking-wide"
        >
          {t("hero.trust")}
        </motion.p>
      </motion.div>
    </section>
  );
}
